import React, { useState, useEffect } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';

interface Meeting {
  id: string;
  title: string;
  description?: string;
  scheduledAt: string;
  meetingType: 'physical' | 'virtual' | 'hybrid';
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  location?: string;
  meetingUrl?: string;
  chamaId: string;
  chamaName?: string;
  attendeeCount?: number;
  duration?: number;
}

interface Attendance {
  userId: string;
  userName?: string;
  isPresent: boolean;
  attendanceType: 'physical' | 'virtual';
}

interface MeetingMinutes {
  content: string;
  status: string;
  takenBy?: string;
  takenByName?: string;
}

interface Document {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadedAt: string;
  url?: string;
}

const MeetingSummary: React.FC = () => {
  const colors = getThemeColors('dark');

  const [loading, setLoading] = useState(true);
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [attendance, setAttendance] = useState<Attendance[]>([]);
  const [minutes, setMinutes] = useState<MeetingMinutes | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  // Mock data for demonstration
  useEffect(() => {
    loadMeetingData();
  }, []);

  const loadMeetingData = async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const mockMeeting: Meeting = {
        id: '1',
        title: 'Monthly Chama Meeting',
        description: 'Regular monthly meeting to discuss contributions and welfare requests',
        scheduledAt: '2025-01-15T14:00:00+03:00',
        meetingType: 'physical',
        status: 'completed',
        location: 'Community Hall, Westlands',
        chamaId: 'chama-1',
        chamaName: 'Savings Group',
        attendeeCount: 15,
        duration: 120
      };

      const mockAttendance: Attendance[] = [
        { userId: '1', userName: 'John Doe', isPresent: true, attendanceType: 'physical' },
        { userId: '2', userName: 'Jane Smith', isPresent: true, attendanceType: 'physical' },
        { userId: '3', userName: 'Bob Johnson', isPresent: false, attendanceType: 'physical' },
        { userId: '4', userName: 'Alice Brown', isPresent: true, attendanceType: 'physical' },
      ];

      const mockMinutes: MeetingMinutes = {
        content: 'Meeting minutes content here...',
        status: 'draft',
        takenBy: 'secretary1',
        takenByName: 'Sarah Wilson'
      };

      const mockDocuments: Document[] = [
        { id: '1', name: 'Meeting Agenda.pdf', size: 245760, type: 'application/pdf', uploadedAt: '2025-01-15T13:30:00Z' },
        { id: '2', name: 'Attendance Sheet.xlsx', size: 51200, type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', uploadedAt: '2025-01-15T15:00:00Z' },
      ];

      setMeeting(mockMeeting);
      setAttendance(mockAttendance);
      setMinutes(mockMinutes);
      setDocuments(mockDocuments);
    } catch (error) {
      console.error('Failed to load meeting data:', error);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadMeetingData();
    setRefreshing(false);
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatTime = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid Time';
    }
  };

  const getMeetingIcon = (type: string): string => {
    switch (type) {
      case 'virtual': return '📹';
      case 'physical': return '🏢';
      case 'hybrid': return '🔄';
      default: return '📅';
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'scheduled': return colors.info;
      case 'in_progress': return colors.warning;
      case 'completed': return colors.success;
      case 'cancelled': return colors.error;
      default: return colors.textSecondary;
    }
  };

  const getStatusIcon = (status: string): string => {
    switch (status) {
      case 'scheduled': return '⏰';
      case 'in_progress': return '🔴';
      case 'completed': return '✅';
      case 'cancelled': return '❌';
      default: return '📅';
    }
  };

  const getAttendanceStats = () => {
    const present = attendance.filter(a => a.isPresent).length;
    const absent = attendance.length - present;
    const rate = attendance.length > 0 ? Math.round((present / attendance.length) * 100) : 0;
    return { present, absent, total: attendance.length, rate };
  };

  const handleDocumentDownload = (document: Document) => {
    if (document.url) {
      window.open(document.url, '_blank');
    } else {
      alert('Document URL not available');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const stats = getAttendanceStats();

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      padding: spacing.md,
      paddingBottom: 100, // Space for tab bar
    },
    header: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.sm,
    },
    headerTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
    },
    backButton: {
      padding: spacing.sm,
      cursor: 'pointer',
      color: colors.text,
    },
    refreshButton: {
      padding: spacing.sm,
      cursor: 'pointer',
      color: colors.primary,
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
    },
    meetingHeaderCard: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('sm'),
    },
    meetingHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: spacing.md,
    },
    meetingTypeIcon: {
      width: 48,
      height: 48,
      borderRadius: 24,
      backgroundColor: colors.primary + '20',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: spacing.lg,
      flexShrink: 0,
    },
    meetingInfo: {
      flex: 1,
    },
    meetingTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.xs,
    },
    meetingDate: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      marginBottom: spacing.xs / 2,
    },
    meetingTime: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginBottom: spacing.xs / 2,
    },
    meetingLocation: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
    },
    meetingDescription: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      lineHeight: 1.5,
      paddingTop: spacing.md,
      borderTop: `1px solid ${colors.border}`,
    },
    section: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('sm'),
    },
    sectionTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      marginBottom: spacing.md,
    },
    overviewCard: {
      marginBottom: spacing.md,
    },
    attendanceStats: {
      display: 'flex',
      justifyContent: 'space-around',
      marginBottom: spacing.md,
      paddingTop: spacing.md,
      paddingBottom: spacing.md,
      borderRadius: borderRadius.md,
      backgroundColor: colors.primary + '10',
    },
    statItem: {
      textAlign: 'center',
    },
    statValue: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.bold,
      marginBottom: spacing.xs / 2,
    },
    statLabel: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
    },
    attendanceCard: {
      marginBottom: spacing.md,
    },
    attendanceList: {
      marginTop: spacing.md,
    },
    attendanceItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: spacing.sm,
      paddingBottom: spacing.sm,
      borderBottom: `1px solid ${colors.border}`,
    },
    attendanceInfo: {
      flex: 1,
    },
    attendeeName: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      color: colors.text,
      marginBottom: spacing.xs / 2,
    },
    attendanceType: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
    },
    attendanceStatus: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: spacing.sm,
      paddingRight: spacing.sm,
      paddingTop: spacing.xs,
      paddingBottom: spacing.xs,
      borderRadius: borderRadius.sm,
    },
    minutesCard: {
      marginBottom: spacing.md,
    },
    minutesContent: {
      fontSize: typography.fontSize.base,
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: spacing.md,
      padding: spacing.md,
      borderRadius: borderRadius.md,
      backgroundColor: colors.backgroundSecondary,
    },
    minutesMetadata: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    minutesStatus: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      color: colors.primary,
    },
    minutesTakenBy: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
    },
    documentsCard: {
      marginBottom: spacing.md,
    },
    documentItem: {
      display: 'flex',
      alignItems: 'center',
      paddingTop: spacing.sm,
      paddingBottom: spacing.sm,
      borderBottom: `1px solid ${colors.border}`,
      cursor: 'pointer',
    },
    documentInfo: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
    },
    documentDetails: {
      marginLeft: spacing.md,
      flex: 1,
    },
    documentName: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      color: colors.text,
      marginBottom: spacing.xs / 2,
    },
    documentType: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: spacing.xl,
      paddingBottom: spacing.xl,
    },
    emptyIcon: {
      fontSize: 48,
      marginBottom: spacing.lg,
      opacity: 0.5,
    },
    emptyText: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing.xxxl,
    },
    skeletonCard: {
      height: 120,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      marginBottom: spacing.md,
      padding: spacing.md,
    },
    skeletonLine: {
      height: 12,
      borderRadius: 6,
      backgroundColor: colors.border,
      marginBottom: spacing.sm,
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.skeletonCard}>
            <div style={{ ...styles.skeletonLine, width: '70%' }} />
            <div style={{ ...styles.skeletonLine, width: '50%' }} />
            <div style={{ ...styles.skeletonLine, width: '30%' }} />
          </div>
          <div style={styles.skeletonCard}>
            <div style={{ ...styles.skeletonLine, width: '60%' }} />
            <div style={{ ...styles.skeletonLine, width: '40%' }} />
            <div style={{ ...styles.skeletonLine, width: '80%' }} />
            <div style={{ ...styles.skeletonLine, width: '55%' }} />
          </div>
          <div style={styles.skeletonCard}>
            <div style={{ ...styles.skeletonLine, width: '45%' }} />
            <div style={{ ...styles.skeletonLine, width: '65%' }} />
          </div>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📄</div>
          <h2 style={styles.headerTitle}>Meeting Data Not Available</h2>
          <p style={styles.emptyText}>Unable to load the meeting summary.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <button
            style={styles.backButton}
            onClick={() => alert('Navigate back to meetings')}
          >
            ←
          </button>
          <h1 style={styles.headerTitle}>Meeting Summary</h1>
          <button
            style={styles.refreshButton}
            onClick={onRefresh}
            disabled={refreshing}
          >
            {refreshing ? '⟳' : '↻'}
          </button>
        </div>
      </div>

      <div style={styles.content}>
        {/* Meeting Header */}
        <div style={styles.meetingHeaderCard}>
          <div style={styles.meetingHeader}>
            <div style={styles.meetingTypeIcon}>
              <span style={{ fontSize: 24 }}>
                {getMeetingIcon(meeting.meetingType)}
              </span>
            </div>

            <div style={styles.meetingInfo}>
              <h3 style={styles.meetingTitle}>{meeting.title}</h3>
              <p style={styles.meetingDate}>{formatDate(meeting.scheduledAt)}</p>
              <p style={styles.meetingTime}>
                {formatTime(meeting.scheduledAt)} • {meeting.meetingType.charAt(0).toUpperCase() + meeting.meetingType.slice(1)}
              </p>
              {meeting.location && <p style={styles.meetingLocation}>📍 {meeting.location}</p>}
            </div>
          </div>

          {meeting.description && (
            <p style={styles.meetingDescription}>{meeting.description}</p>
          )}
        </div>

        {/* Attendance Summary */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Attendance Summary</h2>
          <div style={styles.attendanceStats}>
            <div style={styles.statItem}>
              <div style={{ ...styles.statValue, color: colors.success }}>{stats.present}</div>
              <div style={styles.statLabel}>Present</div>
            </div>
            <div style={styles.statItem}>
              <div style={{ ...styles.statValue, color: colors.error }}>{stats.absent}</div>
              <div style={styles.statLabel}>Absent</div>
            </div>
            <div style={styles.statItem}>
              <div style={{ ...styles.statValue, color: colors.primary }}>{stats.rate}%</div>
              <div style={styles.statLabel}>Rate</div>
            </div>
          </div>
        </div>

        {/* Attendance Details */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Attendance Details</h2>
          {attendance.length > 0 ? (
            <div style={styles.attendanceList}>
              {attendance.map((attendee, index) => (
                <div key={index} style={styles.attendanceItem}>
                  <div style={styles.attendanceInfo}>
                    <div style={styles.attendeeName}>{attendee.userName || `Member ${attendee.userId}`}</div>
                    <div style={styles.attendanceType}>{attendee.attendanceType} attendance</div>
                  </div>
                  <div style={{
                    ...styles.attendanceStatus,
                    backgroundColor: attendee.isPresent ? colors.success + '20' : colors.error + '20'
                  }}>
                    <span style={{ color: attendee.isPresent ? colors.success : colors.error }}>
                      {attendee.isPresent ? '✓' : '✗'}
                    </span>
                    <span style={{
                      color: attendee.isPresent ? colors.success : colors.error,
                      fontSize: typography.fontSize.sm,
                      fontWeight: typography.fontWeight.medium,
                      marginLeft: spacing.xs
                    }}>
                      {attendee.isPresent ? 'Present' : 'Absent'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>👥</div>
              <p style={styles.emptyText}>No attendance data available</p>
            </div>
          )}
        </div>

        {/* Meeting Minutes */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Meeting Minutes</h2>
          {minutes ? (
            <>
              <div style={styles.minutesContent}>{minutes.content}</div>
              <div style={styles.minutesMetadata}>
                <span style={styles.minutesStatus}>Status: {minutes.status?.toUpperCase()}</span>
                <span style={styles.minutesTakenBy}>Recorded by: {minutes.takenByName || 'Unknown'}</span>
              </div>
            </>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📝</div>
              <p style={styles.emptyText}>No meeting minutes available</p>
            </div>
          )}
        </div>

        {/* Meeting Documents */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Meeting Documents ({documents.length})</h2>
          {documents.length > 0 ? (
            documents.map((document) => (
              <div
                key={document.id}
                style={styles.documentItem}
                onClick={() => handleDocumentDownload(document)}
              >
                <div style={styles.documentInfo}>
                  <span style={{ fontSize: 20 }}>📄</span>
                  <div style={styles.documentDetails}>
                    <div style={styles.documentName}>{document.name}</div>
                    <div style={styles.documentType}>
                      {document.type.split('/')[1]?.toUpperCase()} • {formatFileSize(document.size)}
                    </div>
                  </div>
                </div>
                <span style={{ color: colors.primary, fontSize: 18 }}>⬇️</span>
              </div>
            ))
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📁</div>
              <p style={styles.emptyText}>No documents available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingSummary;
